import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useAlert } from "react-alert";
import { contextMenu } from "react-contexify";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Grid,
  Icon,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import BusinessNetworkTree from "./BusinessNetworkTree";
import BusinessNetworkTable from "./BusinessNetworkTable";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import ContextMenu from "./ContextMenu";
import CustomerCardDialog from "../CustomerCardDialog";

import CreateCompanyModal from "../../../common/CreateNameModal/BusinessNetworks";
import CreateNetworkModal from "../../../common/CreateNameModal";
import ExistingCompanyConfirmationModal from "../../../common/ConfirmationModal";
import { GenericAdminPageSkeleton } from "../../../Skeleton";
import getRootTreeData from "./helpers/getRootTreeData";
import { businessNetworkErrorCodes } from "../../../../constant";
import { LIST_BUSINESS_NETWORKS } from "../../../../graphql/queries/CompanyAdminContext";
import {
  CREATE_NETWORK,
  RENAME_NETWORK,
  DELETE_NETWORK,
  ADD_COMPANY,
  CREATE_COMPANY,
  REMOVE_COMPANY,
} from "../../../../graphql/mutations/CompanyAdminContext";

const useStyles = makeStyles(theme => ({
  root: {

  },
  head: {
    padding: "15px 20px",
    borderBottom: "2px solid #F5F5F5",
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
  },
  headTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: "0px",
  },
  headSubTitle: {
    marginTop: "-2px",
    fontSize: 14,
    fontWeight: 400,
  },
  headSubTitleExample: {
    opacity: 0.6,
    display: 'inline',
  },
  headMarginBottom: {
    marginBottom: 0,
  },
  pageHeader: {
    marginBottom: 10,
  },
  contentToolbar: {
    minHeight: 48,
    padding: "7px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    borderBottom: "1px solid #E6E9EE",
    backgroundColor: "#FBFCFD",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
    },
  },
  viewToggle: {
    minHeight: 32,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    "& .MuiToggleButton-root": {
      minHeight: 32,
      padding: "4px 11px",
      borderColor: "#DDE2E8",
      color: "#667085",
      fontSize: 11,
      fontWeight: 500,
      textTransform: "none",
      [theme.breakpoints.down("sm")]: {
        flex: 1,
      },
      "&.Mui-selected": {
        backgroundColor: `${theme.palette.primary.main}0F`,
        color: theme.palette.primary.main,
      },
      "&.Mui-selected:hover": {
        backgroundColor: `${theme.palette.primary.main}17`,
      },
    },
  },
  gridContainer: {
    marginTop: 0,
    backgroundColor: theme.palette.common.white,
    border: "1px solid #E6E9EE",
    boxShadow: "0 1px 2px rgba(16,24,40,.03), 0 8px 24px rgba(16,24,40,.016)",
    borderRadius: 10,
    overflow: "hidden",
  },
  treeViewContent: {
    padding: 15,
  },
  addLink: {
    fontSize: 12,
    color: theme.palette.primary.main,
    //marginLeft: "auto",
    marginLeft: "2em",
    padding: 0,
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "transparent",
      opacity: 0.8,
    },
  },
  addIcon: {
    width: "unset",
    height: "unset",
    overflow: "unset",
    fontSize: 12,
    marginRight: 8,
    marginTop: 3,
  },
  treeItem: {
    "& .MuiTreeItem-label": {
      marginBottom: 5,
    },
  },
  labelRoot: {
    display: "flex",
    alignItems: "center",
    color: "#555",
    textDecoration: "none",
    "&:hover": {
      color: "#555",
    },
    "&:focus": {
      color: "#555",
    },
    "&:active": {
      color: "#555",
    },
  },
  labelIcon: {
    fontSize: 15,
    marginRight: 10,
    width: 15,
    height: 15,
    overflow: "unset",
    textAlign: "center",
    color: "#555",
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "14px",
  },
  parentNetworkHeading: {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: 600,
    marginTop: "1.5em",
    marginBottom: "0.5em",
    "$gridContainer > :nth-child(1) &": {
      marginTop: 0,
    },
  },
  parentNetworkName: {
    "$parentNetworkHeading > &": {
      fontWeight: 600,
      marginRight: "auto",
    },
  },
}));

/*
 * BusinessNetworks is now the canonical CRM component.
 *
 * IMPORTANT:
 * The current project exports LIST_BUSINESS_NETWORKS and
 * businessNetworkErrorCodes, and the GraphQL response is read from
 * companyAdminContext.companyNetworks.listCompanyNetworks.
 *
 * These backend contract names are intentional and must NOT be changed back
 * to CompanyNetworks unless the actual GraphQL/backend contract changes.
 */
const contextMenuId = "business_networks_context_menu";

const BusinessNetworks = ({
  onOpenOrganization,
  organizationOptions,
}) => {
  const classes = useStyles();
  const alert = useAlert();
  const intl = useIntl();

  const { companyId: adminCompanyId, companyName } = useSelector(
    state => state.api.companyAdmin.company
  );

  const [businessNetworks, setBusinessNetworks] = useState();

  const [viewMode, setViewMode] = useState(() => {
    const stored = localStorage.getItem("businessNetworksViewMode");
    return stored === "table" ? "table" : "tree";
  });

  const [selectedNode, setSelectedNode] = useState({});
  const [openedNodes, setOpenedNodes] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [newNetworkName, setNewNetworkName] = useState("");
  const [newParentNetworkId, setNewParentNetworkId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCustomerCardDialog, setShowCustomerCardDialog] = useState(false);
  const [customerCards, setCustomerCards] = useState({});

  const [
    getBusinessNetworks,
    { data: businessNetworksData, loading: businessNetworksLoading },
  ] = useLazyQuery(LIST_BUSINESS_NETWORKS);
  const [addCompany] = useMutation(ADD_COMPANY);
  const [createCompany] = useMutation(CREATE_COMPANY);
  const [removeCompany] = useMutation(REMOVE_COMPANY);
  const [createNetwork] = useMutation(CREATE_NETWORK);
  const [renameNetwork] = useMutation(RENAME_NETWORK);
  const [deleteNetwork] = useMutation(DELETE_NETWORK);

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [
    showExistingCompanyConfirmationModal,
    setShowExistingCompanyConfirmationModal,
  ] = useState(false);
  const [existingCompanyName, setExistingCompanyName] = useState("");

  const [
    existingCompanyPublicCompanyId,
    setExistingCompanyPublicCompanyId,
  ] = useState("");

  const handleSetEditNode = value => {
    setIsEditMode(value);
  };

  const handleOnRename = value => {
    handleSetEditNode(value);
  };

  const handleError = errorResult => {
    const { errorCode } = errorResult;
    if (errorCode === businessNetworkErrorCodes.ALREADY_EXISTS) {
      alert.error(<FormattedMessage id="admin.crm.businessNetworks.alreadyExists" />);
    } else if (errorCode === businessNetworkErrorCodes.NOT_FOUND) {
      alert.error(<FormattedMessage id="admin.crm.businessNetworks.notFound" />);
    } else {
      alert.error(<FormattedMessage id="common.genericErrorMessage" />);
    }
  };

  const handleFetchBusinessNetworks = () => {
    getBusinessNetworks({ variables: { companyId: adminCompanyId } });
  };

  useEffect(() => {
    if (adminCompanyId && adminCompanyId > 0) {
      getBusinessNetworks({ variables: { companyId: adminCompanyId } });      
    }
  }, [adminCompanyId, getBusinessNetworks]);

  useEffect(() => {
    const result =
      businessNetworksData?.companyAdminContext?.companyNetworks
        ?.listCompanyNetworks;

    if (result) {
      const { networks = [], parentNetworks = [] } = result;
      const businessNetworksTree = getRootTreeData({
        networks,
        parentNetworks,
      });
      setBusinessNetworks(businessNetworksTree);
    }
  }, [businessNetworksData]);

  const handleOnNodeToggle = (e, nodeIds) => {
    e.preventDefault();
    setOpenedNodes(nodeIds);
  };

  const handleContextMenu = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    contextMenu.show({
      id: contextMenuId,
      event: e,
    });
    if (selectedNode !== node) {
      setSelectedNode(node);
    }
  };

  const handleOpenCustomerCard = () => {
    if (selectedNode?.isNetwork || !selectedNode?.companyId) {
      return;
    }

    setShowCustomerCardDialog(true);
  };

  const handleOnCreateCompany = async ({ name }) => {
    const {
      isNetwork,
      isRoot,
      networkId,
    } = selectedNode;

    if (!isNetwork) {
      return;
    }

    var variables = {
      companyId: adminCompanyId,
      name,
      networkId,
      parentCompanyId: isRoot ? adminCompanyId : null,
    };

    try {
      const res = await createCompany({ variables });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.businessNetworks &&
        res.data.companyAdminContext.businessNetworks.createCompany
      ) {
        const {
          success,
          result,
          errorResult,
        } = res.data.companyAdminContext.businessNetworks.createCompany;

        if (success) {
          const newResult = { ...result, networkId };
          handleCompanyCreated(newResult);
          //alert.success(<FormattedMessage id="common.genericSuccess" />);
        } else {
          const { data, errorCode } = errorResult;
          if (errorCode === businessNetworkErrorCodes.ALREADY_EXISTS) {
            setExistingCompanyPublicCompanyId(data[0].value);
            setExistingCompanyName(variables.name);
            setShowExistingCompanyConfirmationModal(true);
          } else {
            handleError(errorResult);
          }
        }

        setShowCompanyModal(false);
      }
    } catch (err) {
      console.error("error >", err);
    }
  };

  const addCompanyByPublicCompanyId = async publicCompanyId => {
    const {
      isNetwork,
      isRoot,
      networkId,
    } = selectedNode;

    if (!isNetwork) {
      return;
    }

    var variables = {
      companyId: adminCompanyId,
      networkId,
      parentCompanyId: isRoot ? adminCompanyId : null,
      publicCompanyId: publicCompanyId,
    };

    try {
      const res = await addCompany({ variables });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.businessNetworks &&
        res.data.companyAdminContext.businessNetworks.addCompany
      ) {
        const {
          success,
          result,
          errorResult,
        } = res.data.companyAdminContext.businessNetworks.addCompany;

        if (success) {
          const newResult = { ...result, networkId };
          handleCompanyCreated(newResult);
          //alert.success(<FormattedMessage id="common.genericSuccess" />);
        } else {
          handleError(errorResult);
        }

        setShowCompanyModal(false);
        setShowExistingCompanyConfirmationModal(false);
      }
    } catch (err) {
      console.error("error >", err);
    }
  };

  const handleCompanyCreated = newCompany => {
    const { memberId: compId, networkId } = selectedNode;
    const updatedNewCompany = {
      ...newCompany,
      parentCompanyId: compId,
      isNew: true,
    };
    const { memberId: newCompanyId } = updatedNewCompany;

    let newNetworkTree = { ...businessNetworks };
    let id = null;

    if (compId) {
      id = `c_${compId}`;
    } else {
      id = networkId;
    }

    if (id) {
      const hasChildren =
        Array.isArray(newNetworkTree[id].childCompanies) &&
        newNetworkTree[id].childCompanies.length > 0;

      let newChildCompanies = [];
      let newChildCompanyIds = [];

      if (hasChildren) {
        newChildCompanies = [
          ...newNetworkTree[id].childCompanies,
          updatedNewCompany,
        ];
        newChildCompanyIds = [
          ...newNetworkTree[id].childCompanyIds,
          newCompanyId,
        ];
      } else {
        newChildCompanies = [updatedNewCompany];
        newChildCompanyIds = [newCompanyId];
      }

      const newCompanyData = {
        ...newNetworkTree[id],
        childCompanyIds: newChildCompanyIds,
        childCompanies: newChildCompanies,
      };

      newNetworkTree = {
        ...newNetworkTree,
        [id]: newCompanyData,
        [`c_${newCompanyId}`]: updatedNewCompany,
      };
    }

    setBusinessNetworks(newNetworkTree);
  };

  const handleNetworkCreated = newNetwork => {
    let newNetworkTree = { ...businessNetworks };

    const { networkId: newNetworkId } = newNetwork;
    const newTempNetwork = { ...newNetwork, isNetwork: true, isRoot: false };

    newNetworkTree = {
      ...newNetworkTree,
      [newNetworkId]: newTempNetwork,
    };

    setBusinessNetworks(newNetworkTree);
  };

  const handleRenameNetwork = async (name, networkId) => {
    try {
      const res = await renameNetwork({
        variables: {
          companyId: adminCompanyId,
          networkId,
          name,
        },
      });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.businessNetworks &&
        res.data.companyAdminContext.businessNetworks.renameNetwork
      ) {
        const {
          success,
          errorResult,
        } = res.data.companyAdminContext.businessNetworks.renameNetwork;

        if (success) {
          handleNetworkRenamed({ networkId, name });
          //alert.success(<FormattedMessage id="common.genericSuccess" />);
        } else {
          handleError(errorResult);
        }

        return success;
      }
    } catch (err) {
      console.error("error >", err);
    }
  };

  const handleNetworkRenamed = ({ networkId, name }) => {
    const newBusinessNetworks = { ...businessNetworks };
    newBusinessNetworks[networkId].name = name;
    setBusinessNetworks(newBusinessNetworks);
  };

  const handleCreateNetwork = async () => {
    try {
      const variables = {
        companyId: adminCompanyId,
        name: newNetworkName,
        parentNetworkId: newParentNetworkId,
      };

      const res = await createNetwork({ variables });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.businessNetworks &&
        res.data.companyAdminContext.businessNetworks.createNetwork
      ) {
        const {
          success,
          result,
          errorResult,
        } = res.data.companyAdminContext.businessNetworks.createNetwork;

        if (success) {
          handleNetworkCreated(result);
          //alert.success(<FormattedMessage id="common.genericSuccess" />);
        } else {
          handleError(errorResult);
        }

        setShowNetworkModal(false);
      }
    } catch (err) {
      console.error("error >", err);
      alert.error(<FormattedMessage id="common.genericErrorMessage" />);
    }
  };

  const handleNetworkDeleted = () => {
    const { networkId } = selectedNode;
    const newBusinessNetworks = { ...businessNetworks };
    delete newBusinessNetworks[networkId];
    setBusinessNetworks(newBusinessNetworks);
  };

  const handleDeleteNetwork = async networkId => {
    try {
      const res = await deleteNetwork({
        variables: {
          companyId: adminCompanyId,
          networkId,
        },
      });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.businessNetworks &&
        res.data.companyAdminContext.businessNetworks.deleteNetwork
      ) {
        const {
          success,
          errorResult,
        } = res.data.companyAdminContext.businessNetworks.deleteNetwork;

        if (success) {
          handleNetworkDeleted();
          //alert.success(<FormattedMessage id="common.genericSuccess" />);
        } else {
          handleError(errorResult);
        }

        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error("error >", err);
    }
  };

  const handleCompanyRemoved = () => {
    const { parentCompanyId, memberId: compId, networkId } = selectedNode;
    const key = parentCompanyId ? `c_${parentCompanyId}` : `${networkId}`;

    let newBusinessNetworks = { ...businessNetworks };
    let newChildCompanyIds = [];
    let newChildCompanies = [];

    if (
      Array.isArray(newBusinessNetworks[key].childCompanyIds) &&
      newBusinessNetworks[key].childCompanyIds.length > 0
    ) {
      newChildCompanyIds = newBusinessNetworks[key].childCompanyIds.filter(
        x => x !== compId
      );
      newChildCompanies = newBusinessNetworks[key].childCompanies.filter(
        x => x.memberId !== compId
      );
    }

    const newCompanyData = {
      ...newBusinessNetworks[key],
      childCompanyIds: newChildCompanyIds,
      childCompanies: newChildCompanies,
    };

    newBusinessNetworks = {
      ...newBusinessNetworks,
      [key]: newCompanyData,
    };

    setBusinessNetworks(newBusinessNetworks);
  };

  const handleRemoveCompany = async (networkId, targetCompanyId) => {
    try {
      const res = await removeCompany({
        variables: {
          companyId: adminCompanyId,
          networkId,
          targetCompanyId,
        },
      });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.businessNetworks &&
        res.data.companyAdminContext.businessNetworks.removeCompany
      ) {
        const {
          success,
          errorResult,
        } = res.data.companyAdminContext.businessNetworks.removeCompany;

        if (success) {
          handleCompanyRemoved();
          //alert.success(<FormattedMessage id="common.genericSuccess" />);
        } else {
          handleError(errorResult);
        }

        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error("error >", err);
    }
  };

  const handleOpenNetworkModal = parentNetworkId => {
    setShowNetworkModal(true);
    setNewParentNetworkId(parentNetworkId);
    setNewNetworkName("");
  };

  const handleCreateCompanyClicked = networkId => {
    var node = businessNetworks[networkId];
    setSelectedNode(node);
    setShowCompanyModal(true);
  };

  const handleOnAddCompany = async ({ publicCompanyId }) => {
    await addCompanyByPublicCompanyId(publicCompanyId);
  };

  const handleAddExistingCompany = async () => {
    await addCompanyByPublicCompanyId(existingCompanyPublicCompanyId);
  };

  const handleViewModeChange = (event, value) => {
    if (!value) {
      return;
    }

    setViewMode(value);
    localStorage.setItem("businessNetworksViewMode", value);
  };

  const PageTitle = () => {
    return (
      <Box className={classes.headMarginBottom} xs={2}>
        <Typography className={classes.headTitle}><FormattedMessage id="admin.crm.businessNetworks" /></Typography>
        <Typography className={classes.headSubTitle}><FormattedMessage id="admin.crm.businessNetworks.subTitle" /><Typography display="inline" className={classes.headSubTitleExample}> <FormattedMessage id="admin.crm.businessNetworks.subTitle.Example" /></Typography></Typography>
      </Box>
    );
  };

  const ParentNetworkHeading = ({ parentNetwork }) => {
    return (
      <Typography className={classes.parentNetworkHeading}>
        <Typography className={classes.parentNetworkName}>
          {parentNetwork.name}
        </Typography>
        <Button
          className={classes.addLink}
          onClick={() => handleCreateCompanyClicked(parentNetwork.networkId)}
        >
          <Icon className={clsx(["fas fa-plus", classes.addIcon])} />
          <FormattedMessage id="admin.crm.businessNetworks.newCompany" />
        </Button>
        <Button
          className={classes.addLink}
          onClick={() => handleOpenNetworkModal(parentNetwork.networkId)}
        >
          <Icon className={clsx(["fas fa-plus", classes.addIcon])} />
          <FormattedMessage id="admin.crm.businessNetworks.newNetwork" />
        </Button>
      </Typography>
    );
  };

  const customerCardOrganizationOptions = useMemo(() => {
    const byId = new Map();

    Object.values(businessNetworks || {})
      .filter(
        node =>
          node &&
          !node.isNetwork &&
          node.companyId
      )
      .forEach(node => {
        byId.set(node.companyId, node);
      });

    (organizationOptions || []).forEach(option => {
      const id =
        option?.companyId ??
        option?.organizationId ??
        option?.id;

      if (!id) {
        return;
      }

      byId.set(id, {
        ...(byId.get(id) || {}),
        ...option,
      });
    });

    return [...byId.values()];
  }, [businessNetworks, organizationOptions]);

  if (businessNetworksLoading || !businessNetworks) {
    return <GenericAdminPageSkeleton title={<PageTitle />} xs={12} />;
  }

  const rootNetworks = Object.values(businessNetworks).filter(
    n => n.isNetwork && n.isRoot
  );
  const haveRootNetworks = rootNetworks.length > 0;

  return (
    <Grid container spacing={4}>
      <Grid item className={classes.root} xs={12}>
        <Box className={classes.pageHeader}>
          <PageTitle />
        </Box>

        <Box className={classes.gridContainer}>
          <Box className={classes.contentToolbar}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={handleViewModeChange}
              className={classes.viewToggle}
              aria-label={intl.formatMessage({
                id: "admin.crm.businessNetworks.view.label",
                defaultMessage: "Visningsläge",
              })}
            >
              <ToggleButton value="tree">
                <Stack direction="row" spacing={0.55} alignItems="center">
                  <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                  <FormattedMessage
                    id="admin.crm.businessNetworks.view.tree"
                    defaultMessage="Trädvy"
                  />
                </Stack>
              </ToggleButton>

              <ToggleButton value="table">
                <Stack direction="row" spacing={0.55} alignItems="center">
                  <TableRowsOutlinedIcon sx={{ fontSize: 16 }} />
                  <FormattedMessage
                    id="admin.crm.businessNetworks.view.table"
                    defaultMessage="Tabellvy"
                  />
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {viewMode === "tree" && (
            <Box className={classes.treeViewContent}>
              {!haveRootNetworks && (
                <Box>
                  <BusinessNetworkTree
                    handleContextMenu={handleContextMenu}
                    handleError={handleError}
                    handleOnNodeToggle={handleOnNodeToggle}
                    handleRenameNetwork={handleRenameNetwork}
                    handleSetEditNode={handleSetEditNode}
                    isEditMode={isEditMode}
                    openedNodes={openedNodes}
                    parentNetworkId={null}
                    refresh={handleFetchBusinessNetworks}
                    selectedNode={selectedNode}
                    treeData={businessNetworks}
                  />
                </Box>
              )}

              {rootNetworks.map(n => (
                <Box key={`root-network-${n.networkId}`}>
                  <ParentNetworkHeading parentNetwork={n} />
                  <BusinessNetworkTree
                    handleContextMenu={handleContextMenu}
                    handleError={handleError}
                    handleOnNodeToggle={handleOnNodeToggle}
                    handleRenameNetwork={handleRenameNetwork}
                    handleSetEditNode={handleSetEditNode}
                    isEditMode={isEditMode}
                    openedNodes={openedNodes}
                    parentNetworkId={n.networkId}
                    refresh={handleFetchBusinessNetworks}
                    selectedNode={selectedNode}
                    treeData={businessNetworks}
                  />
                </Box>
              ))}
            </Box>
          )}

          {viewMode === "table" && (
            <BusinessNetworkTable
              customerCards={customerCards}
              handleContextMenu={handleContextMenu}
              treeData={businessNetworks}
            />
          )}
        </Box>

        <ContextMenu
          contextMenuId={contextMenuId}
          customerCardExists={Boolean(selectedNode?.companyInvoiceCardId)}
        onCustomerCard={handleOpenCustomerCard}
        onDelete={() => {
          setShowDeleteModal(true);
        }}
        onNewCompany={() => {
          setShowCompanyModal(true);
        }}
        onRename={handleOnRename}
        selectedNode={selectedNode}
      />

      {showNetworkModal && (
        <CreateNetworkModal
          handleClose={() => {
            setShowNetworkModal(false);
          }}
          onChange={e => setNewNetworkName(e.target.value)}
          onClick={handleCreateNetwork}
          open={showNetworkModal}
          placeholder={intl.formatMessage({ id: "admin.crm.businessNetworks.newNetwork" })}
          title={intl.formatMessage({ id: "admin.crm.businessNetworks.newNetwork" })}
          value={newNetworkName}
        />
      )}

      {showCompanyModal && (
        <CreateCompanyModal
          onAddCompany={handleOnAddCompany}
          onClose={() => {
            setShowCompanyModal(false);
          }}
          onCreateCompany={handleOnCreateCompany}
          open={showCompanyModal}
          placeholder={intl.formatMessage({ id: "context.newCompany" })}
          title={intl.formatMessage({ id: "context.newCompany" })}
        />
      )}

      {showExistingCompanyConfirmationModal && (
        <ExistingCompanyConfirmationModal
          handleClose={() => {
            setShowExistingCompanyConfirmationModal(false);
          }}
          message={
            <FormattedMessage
              id="admin.crm.businessNetworks.addExistingCompany"
              values={{ company: existingCompanyName }}
            />
          }
          onClick={handleAddExistingCompany}
          open={showExistingCompanyConfirmationModal}
        />
      )}

      {showCustomerCardDialog && (
          <CustomerCardDialog
            companyId={adminCompanyId}
          onClose={() => {
            setShowCustomerCardDialog(false);
          }}
            onOpenOrganization={onOpenOrganization}
            onSaveComplete={() => { handleFetchBusinessNetworks() }}
          open={showCustomerCardDialog}
            cardId={selectedNode?.companyInvoiceCardId || 0}
          organization={{ 
            
            companyId: adminCompanyId,

            // TODO: Hardcoded for testing
            name: companyName,
          }}
          organizationOptions={
            customerCardOrganizationOptions
          }
            
        />
      )}

      {showDeleteModal && (
        <ConfirmDeleteModal
          businessNetworks={businessNetworks}
          handleDeleteNetwork={handleDeleteNetwork}
          handleRemoveCompany={handleRemoveCompany}
          open={showDeleteModal}
          selectedNode={selectedNode}
          setOpen={setShowDeleteModal}
        />
      )}
      </Grid>
    </Grid>
  );
};

BusinessNetworks.propTypes = {
  onOpenOrganization: PropTypes.func,
  organizationOptions: PropTypes.arrayOf(
    PropTypes.object
  ),
};

BusinessNetworks.defaultProps = {
  onOpenOrganization: null,
  organizationOptions: [],
};

export default BusinessNetworks;
